/**
 * Role templates (docs/13-ACCESS.md, "Assigning access"): a named bundle of
 * grants — module and level, church-wide or "where you choose when you
 * apply it" — that fills a person's Permissions rows in one go. They replace
 * the custom-role editor.
 *
 * A template grants nothing by itself. Applying one gives each of its
 * grants (each checked by `canGrant`, on the server too), and never lowers a
 * level someone already holds at the same place.
 *
 * Three are built in; a church adds its own (stored, `access_templates`).
 */
import {
  ASSIGNABLE_MODULES,
  grantOf,
  grantPlaceKey,
  isAccessModule,
  isLevelOf,
  levelRank,
  type AccessGrant,
  type AccessModule,
  type GrantScope,
  type Level,
} from './access.ts'

/**
 * Where a template's grant applies: church-wide, or the place chosen when
 * it is applied (a folder or service type, or church-wide). Only planning
 * has places; every other module is church-wide.
 */
export type TemplateScope = 'org' | 'pick'

export interface TemplateGrant {
  module: AccessModule
  level: Level
  scope: TemplateScope
}

export interface RoleTemplate {
  /** `builtin:…` for the built-in ones, the row's id for a church's own. */
  id: string
  name: string
  description: string | null
  grants: readonly TemplateGrant[]
  builtIn: boolean
}

/** At most this many grants in one template (one per module and scope is plenty). */
export const MAX_TEMPLATE_GRANTS = 12

export const BUILT_IN_TEMPLATES: readonly RoleTemplate[] = [
  {
    id: 'builtin:worship_leader',
    name: 'Worship leader',
    description: 'Plans the services and keeps the song library',
    builtIn: true,
    grants: [
      { module: 'planning', level: 'editor', scope: 'pick' },
      { module: 'songs', level: 'editor', scope: 'org' },
    ],
  },
  {
    id: 'builtin:scheduler',
    name: 'Scheduler',
    description: 'Schedules the teams and sends the requests',
    builtIn: true,
    grants: [
      { module: 'planning', level: 'scheduler', scope: 'pick' },
      { module: 'songs', level: 'viewer', scope: 'org' },
    ],
  },
  {
    id: 'builtin:campus_admin',
    name: 'Campus admin',
    description: 'Runs a campus’s services, gives access there, and reads the directory',
    builtIn: true,
    grants: [
      { module: 'planning', level: 'administrator', scope: 'pick' },
      { module: 'songs', level: 'viewer', scope: 'org' },
      { module: 'people', level: 'viewer', scope: 'org' },
    ],
  },
]

/**
 * A template grant from loose values (a request body, a stored row), or
 * null: an unknown module, a level off its ladder, or "choose a place" on a
 * module that has no places.
 */
export function parseTemplateGrant(raw: {
  module: string
  level: string
  scope: string
}): TemplateGrant | null {
  if (!isAccessModule(raw.module) || !isLevelOf(raw.module, raw.level)) return null
  if (raw.scope !== 'org' && raw.scope !== 'pick') return null
  if (raw.scope === 'pick' && raw.module !== 'planning') return null
  return { module: raw.module, level: raw.level, scope: raw.scope }
}

/**
 * The problem with a template's grants, or null when they make one: at least
 * one, not too many, each valid, and one rung per module and scope (two
 * would only fight over the same place).
 */
export function templateGrantsError(
  grants: readonly { module: string; level: string; scope: string }[],
): string | null {
  if (grants.length === 0) return 'A template gives at least one level.'
  if (grants.length > MAX_TEMPLATE_GRANTS)
    return `A template gives at most ${MAX_TEMPLATE_GRANTS} levels.`
  const seen = new Set<string>()
  for (const raw of grants) {
    const grant = parseTemplateGrant(raw)
    if (!grant) return 'That is not a level of that module, or not somewhere it can be given.'
    const key = `${grant.module}:${grant.scope}`
    if (seen.has(key)) return 'A template gives each module once in each place.'
    seen.add(key)
  }
  return null
}

/** Whether applying it asks where (a folder, a service type, or church-wide). */
export const needsPlace = (template: Pick<RoleTemplate, 'grants'>): boolean =>
  template.grants.some((grant) => grant.scope === 'pick')

/**
 * The grants a template gives when applied at `place` (its "choose a place"
 * grants go there; the rest are church-wide).
 */
export function templateGrants(
  template: Pick<RoleTemplate, 'grants'>,
  place: GrantScope = { kind: 'org' },
): AccessGrant[] {
  return template.grants.map((grant) =>
    grantOf(grant.module, grant.level, grant.scope === 'pick' ? place : { kind: 'org' }),
  )
}

/**
 * What applying changes: the proposed grants, less any the person already
 * holds at the same place at that level or higher. A template adds and
 * raises; it never lowers what someone has.
 */
export function grantsToApply(
  held: readonly AccessGrant[],
  proposed: readonly AccessGrant[],
): AccessGrant[] {
  const best = new Map<string, Level>()
  for (const grant of held) {
    const key = grantPlaceKey(grant)
    const level = best.get(key)
    if (!level || levelRank(grant.level) > levelRank(level)) best.set(key, grant.level)
  }
  return proposed.filter((grant) => {
    const level = best.get(grantPlaceKey(grant))
    return !level || levelRank(grant.level) > levelRank(level)
  })
}

/** The modules a template can carry: those access is given on today. */
export const TEMPLATE_MODULES: readonly AccessModule[] = ASSIGNABLE_MODULES
